import React, { useEffect, useState } from 'react';
import { useTranslation } from '@jobcost-monorepo/i18n';
import { Button, FileUpload, Loader, useAuth } from '@jobcost-monorepo/ui';
import {
  ApiResponse,
  CustomApiClient,
  handleGlobalError,
  IGestifyCredential,
  IListFetchParams,
  IListGestifyCredentialFetchParams,
  IListResponse,
  LONG_REQUEST_TIMEOUT_MS,
  MN_GESTIFY_CREDENTIALS,
} from '@jobcost-monorepo/crud';
import { createRepository } from '@jobcost-monorepo/crud/dto/repositoryFactory';
import { gestifyAuthenticate } from '@jobcost-monorepo/gestify';
import {
  AutofatturaCreateResult,
  AutofatturaItem,
  AutofatturaPreview,
  AutofatturaSezionale,
} from './types';

type Step = 'upload' | 'preview' | 'result';

const PREVIEW_URL = 'gestify-autofatture/preview';
const CREATE_URL = 'gestify-autofatture/create';
const SEZIONALI_URL = 'gestify-sezionali';

// Limiti upload, allineati al backend (multer) e a `client_max_body_size` di Nginx (50 MB).
const MAX_FILE_BYTES = 5 * 1024 * 1024; // dimensione massima del singolo file
const MAX_TOTAL_BYTES = 50 * 1024 * 1024; // dimensione massima della somma dei file
const MAX_FILES = 20; // numero massimo di file per upload
const MB = 1024 * 1024;

const AutofatturePage: React.FC = () => {
  const { t } = useTranslation(['autofatture']);
  const { user } = useAuth();

  const [step, setStep] = useState<Step>('upload');
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<AutofatturaPreview[]>([]);
  const [results, setResults] = useState<AutofatturaCreateResult[]>([]);
  // Sezionali attivi di Gestify e scelta corrente per file (chiave: fileName).
  const [sezionali, setSezionali] = useState<AutofatturaSezionale[]>([]);
  const [sezionaleIds, setSezionaleIds] = useState<Record<string, number>>({});
  const [gestifyToken, setGestifyToken] = useState<string>();

  const oversizedFile = files.find((f) => f.size > MAX_FILE_BYTES);
  const totalSizeExceeded =
    files.reduce((sum, f) => sum + f.size, 0) > MAX_TOTAL_BYTES;
  const tooManyFiles = files.length > MAX_FILES;
  const selectionInvalid = !!oversizedFile || totalSizeExceeded || tooManyFiles;

  const validPreviews = previews.filter((p) => p.valid);
  // Creabili = validi e non già presenti in Gestify (anti-duplicato).
  const creatablePreviews = validPreviews.filter((p) => !p.duplicate?.exists);
  const successResults = results.filter((r) => r.success);

  /** Sezionale in uso per un file: correzione dell'utente o quello dedotto. */
  const selectedSezionale = (
    p: AutofatturaPreview,
  ): AutofatturaSezionale | undefined => {
    const id = sezionaleIds[p.fileName] ?? p.sezionale?.id;
    if (!id) return undefined;
    const match = sezionali.find((s) => s.id === id);
    if (!match) return p.sezionale;
    return {
      ...match,
      // L'avviso resta solo finché è il valore dedotto automaticamente.
      incerto: id === p.sezionale?.id && p.sezionale?.incerto,
    };
  };

  /**
   * Sezionale nella forma accettata da `AutofatturaSezionaleDto`. Il backend
   * valida con `forbidNonWhitelisted`, quindi ogni campo in più (`incerto`, o
   * i metadati di Gestify) farebbe rifiutare l'intera richiesta con un 400.
   */
  const sezionalePayload = (
    p: AutofatturaPreview,
  ): AutofatturaSezionale | undefined => {
    const sezionale = selectedSezionale(p);
    if (!sezionale) return undefined;
    const { id, sigla, descrizione } = sezionale;
    return { id, sigla, descrizione };
  };

  // Evita che il browser apra il file se rilasciato fuori dall'area di upload.
  useEffect(() => {
    const prevent = (e: DragEvent) => {
      e.preventDefault();
    };
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, []);

  const showError = (description: string) =>
    handleGlobalError({
      code: 999,
      message: t('errors.title'),
      description,
      responseMessage: description,
    });

  /**
   * Token Gestify per il batch: ottenuto all'anteprima e riusato alla conferma.
   * I token Gestify durano 4 ore, mentre rifare il login a ogni passo aggiunge
   * due round-trip e un punto in cui la richiesta può impiantarsi.
   */
  const ensureGestifyToken = async (): Promise<string | undefined> => {
    if (gestifyToken) return gestifyToken;
    const token = await loginGestify();
    if (token) setGestifyToken(token);
    return token;
  };

  /** Login a Gestify con le credenziali dell'utente → token. */
  const loginGestify = async (): Promise<string | undefined> => {
    const repository = createRepository<
      IGestifyCredential,
      IListGestifyCredentialFetchParams
    >(MN_GESTIFY_CREDENTIALS);
    const response: ApiResponse<IListResponse<IGestifyCredential>> =
      await repository.getMany({ userId: user?.id });
    const credential = response?.data?.data?.[0];
    if (!credential) {
      showError(t('errors.noCredential'));
      return undefined;
    }
    const token = await gestifyAuthenticate({
      username: credential.username,
      password: credential.password,
    });
    if (!token) {
      showError(t('errors.loginFailed'));
      return undefined;
    }
    return token;
  };

  /** Sezionali attivi di Gestify, per la select di correzione in anteprima. */
  const fetchSezionali = async (token: string) => {
    try {
      const client = new CustomApiClient();
      const response = await client.getWithParams<
        { data?: AutofatturaSezionale[] },
        IListFetchParams & { token: string; attivo: boolean }
      >(SEZIONALI_URL, { token, attivo: true });
      // Solo i campi che ci servono: le righe di Gestify portano anche
      // metadati (utente_created_id, attivo, posizione, created, ...) che non
      // devono finire nel payload di creazione.
      setSezionali(
        (response?.data?.data ?? []).map(({ id, sigla, descrizione }) => ({
          id,
          sigla,
          descrizione,
        })),
      );
    } catch {
      // Non bloccante: senza lista resta il sezionale dedotto dal backend.
      setSezionali([]);
    }
  };

  const onlyXml = (list: File[]) =>
    list.filter((f) => f.name.toLowerCase().endsWith('.xml'));

  const totalSize = (list: File[]) => list.reduce((sum, f) => sum + f.size, 0);

  /**
   * Primo problema della selezione (numero file, dimensione del singolo file,
   * dimensione totale), oppure `undefined` se è valida.
   */
  const selectionError = (list: File[]): string | undefined => {
    if (list.length > MAX_FILES) {
      return t('errors.tooManyFiles', { max: MAX_FILES });
    }
    const oversized = list.find((f) => f.size > MAX_FILE_BYTES);
    if (oversized) {
      return t('errors.fileTooLarge', {
        name: oversized.name,
        max: Math.round(MAX_FILE_BYTES / MB),
      });
    }
    if (totalSize(list) > MAX_TOTAL_BYTES) {
      return t('errors.totalTooLarge', {
        max: Math.round(MAX_TOTAL_BYTES / MB),
      });
    }
    return undefined;
  };

  const isSelectionOk = (list: File[]): boolean => {
    const error = selectionError(list);
    if (error) showError(error);
    return !error;
  };

  const applySelection = (list: File[]) => {
    const xml = onlyXml(list);
    setFiles(xml);
    isSelectionOk(xml);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files ? Array.from(e.target.files) : [];
    applySelection(selected);
  };

  const handleDropFiles = (list: FileList) => {
    applySelection(Array.from(list));
  };

  const handlePreview = async () => {
    if (files.length === 0) {
      showError(t('errors.noFiles'));
      return;
    }
    if (!isSelectionOk(files)) return;
    setLoading(true);
    try {
      const token = await ensureGestifyToken();
      if (!token) return;

      const formData = new FormData();
      formData.append('token', token);
      files.forEach((file) => formData.append('files', file));

      const client = new CustomApiClient();
      const response = await client.postFormData<AutofatturaPreview[]>(
        PREVIEW_URL,
        formData,
        LONG_REQUEST_TIMEOUT_MS,
      );
      const list = (response?.data ?? []) as AutofatturaPreview[];
      setPreviews(list);
      setSezionaleIds(
        Object.fromEntries(
          list
            .filter((p) => p.sezionale?.id)
            .map((p) => [p.fileName, p.sezionale!.id]),
        ),
      );
      await fetchSezionali(token);
      setStep('preview');
    } catch {
      showError(t('errors.previewFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (creatablePreviews.length === 0) return;
    setLoading(true);
    try {
      const token = await ensureGestifyToken();
      if (!token) return;

      const items: AutofatturaItem[] = creatablePreviews.map((p) => ({
        fileName: p.fileName,
        tipoSub: p.tipoSub as string,
        xmlModalita: p.xmlModalita ?? '',
        dataOperazione: p.dataOperazione as string,
        dataRegistrazione: p.dataRegistrazione ?? (p.dataOperazione as string),
        numeroFornitore: p.numeroFornitore,
        supplier: p.supplier!,
        anagrafica: p.anagrafica ?? { found: false },
        sezionale: sezionalePayload(p),
        righe: p.righe ?? [],
      }));

      const client = new CustomApiClient();
      const response = await client.create<{
        token: string;
        items: AutofatturaItem[];
      }>(CREATE_URL, { token, items }, LONG_REQUEST_TIMEOUT_MS);
      setResults(
        ((response?.data as unknown) ?? []) as AutofatturaCreateResult[],
      );
      setStep('result');
    } catch {
      showError(t('errors.createFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFiles([]);
    setPreviews([]);
    setResults([]);
    setSezionali([]);
    setSezionaleIds({});
    setGestifyToken(undefined);
    setStep('upload');
  };

  return (
    <div className="mx-auto w-full max-w-5xl p-4 dark:text-white">
      <h1 className="text-2xl font-semibold">{t('title')}</h1>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {t('subtitle')}
      </p>

      <Loader showLoader={loading} />

      {/* STEP UPLOAD */}
      {step === 'upload' && !loading && (
        <div className="mt-6">
          <FileUpload
            loading={loading}
            handleFileChange={handleFileChange}
            onDropFiles={handleDropFiles}
            multiple
            accept=".xml,text/xml,application/xml"
          />
          {files.length > 0 && (
            <ul className="mt-4 space-y-1 text-sm">
              {files.map((file) => (
                <li
                  key={file.name}
                  className="rounded bg-gray-50 px-3 py-1 dark:bg-grey"
                >
                  {file.name}
                </li>
              ))}
            </ul>
          )}
          {selectionInvalid && (
            <p className="mt-3 text-sm text-red-600">{selectionError(files)}</p>
          )}
          <div className="mt-6">
            <Button
              onClick={handlePreview}
              disabled={files.length === 0 || selectionInvalid}
            >
              {t('actions.preview')}
            </Button>
          </div>
        </div>
      )}

      {/* STEP PREVIEW */}
      {step === 'preview' && !loading && (
        <div className="mt-6">
          <h2 className="text-lg font-medium">{t('preview.title')}</h2>
          <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
            {t('preview.description')}
          </p>
          <div className="space-y-3">
            {previews.map((p) => (
              <PreviewCard
                key={p.fileName}
                preview={p}
                sezionale={selectedSezionale(p)}
                sezionali={sezionali}
                onSezionaleChange={(id) =>
                  setSezionaleIds((prev) => ({ ...prev, [p.fileName]: id }))
                }
                t={t}
              />
            ))}
          </div>
          <div className="mt-6 flex items-center gap-3">
            <Button
              onClick={handleConfirm}
              disabled={creatablePreviews.length === 0}
            >
              {t('actions.confirm')}
            </Button>
            <Button variant="outline" onClick={handleReset}>
              {t('actions.reset')}
            </Button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t('preview.validCount', {
                creatable: creatablePreviews.length,
                total: previews.length,
              })}
            </span>
          </div>
        </div>
      )}

      {/* STEP RESULT */}
      {step === 'result' && !loading && (
        <div className="mt-6">
          <h2 className="text-lg font-medium">{t('result.title')}</h2>
          <div className="mt-4 space-y-3">
            {results.map((r) => (
              <ResultCard key={r.fileName} result={r} t={t} />
            ))}
          </div>
          <div className="mt-6 flex items-center gap-3">
            <Button onClick={handleReset}>{t('actions.reset')}</Button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t('result.successCount', {
                success: successResults.length,
                total: results.length,
              })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div>
    <span className="text-xs uppercase text-gray-400">{label}</span>
    <div className="text-sm">{value ?? '—'}</div>
  </div>
);

interface CardTProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string, options?: any) => string;
}

/** Etichetta leggibile di un sezionale: «Acquisto estero intra UE (INT)». */
const sezionaleLabel = (s: AutofatturaSezionale): string => {
  const descrizione = s.descrizione?.trim();
  const sigla = s.sigla?.trim();
  if (descrizione && sigla) return `${descrizione} (${sigla})`;
  return descrizione || sigla || `#${s.id}`;
};

interface PreviewCardProps {
  preview: AutofatturaPreview;
  sezionale?: AutofatturaSezionale;
  sezionali: AutofatturaSezionale[];
  onSezionaleChange: (id: number) => void;
}

const PreviewCard: React.FC<PreviewCardProps & CardTProps> = ({
  preview,
  sezionale,
  sezionali,
  onSezionaleChange,
  t,
}) => {
  if (!preview.valid) {
    return (
      <div className="rounded-lg border border-red-300 bg-red-50 p-4 dark:bg-grey">
        <div className="font-medium text-red-700">{preview.fileName}</div>
        <div className="text-sm text-red-600">
          {t('preview.invalidFile')}: {preview.error}
        </div>
      </div>
    );
  }
  const s = preview.supplier;
  const found = preview.anagrafica?.found;
  const isDuplicate = preview.duplicate?.exists;
  return (
    <div
      className={`rounded-lg border bg-white p-4 dark:bg-grey ${
        isDuplicate
          ? 'border-amber-300 opacity-80'
          : 'border-gray-200 dark:border-purple'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium">{preview.fileName}</div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {isDuplicate && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
              {t('preview.duplicate', {
                docNumber: preview.duplicate?.docNumber,
              })}
            </span>
          )}
          <span
            className={`rounded-full px-2 py-0.5 text-xs ${
              found
                ? 'bg-green-100 text-green-700'
                : 'bg-amber-100 text-amber-700'
            }`}
          >
            {/* La ragione sociale agganciata va mostrata: fornitori diversi
                possono condividere la stessa P.IVA segnaposto. */}
            {found
              ? t('preview.anagraficaFoundNamed', {
                  name:
                    preview.anagrafica?.ragioneSociale ??
                    s?.ragioneSociale ??
                    '',
                })
              : t('preview.anagraficaToCreate')}
          </span>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Field label={t('preview.supplier')} value={s?.ragioneSociale} />
        <Field label={t('preview.vat')} value={s?.partitaIva} />
        <Field
          label={t('preview.docType')}
          value={`${preview.tipoDocumento ?? ''} ${preview.tipoSub ? `(${preview.tipoSub})` : ''}`}
        />
        <Field label={t('preview.docNumber')} value={preview.numeroFornitore} />
        <Field label={t('preview.date')} value={preview.dataOperazione} />
        <Field
          label={t('preview.total')}
          value={
            preview.totale !== undefined ? `€ ${preview.totale}` : undefined
          }
        />
        <Field label={t('preview.rows')} value={preview.righe?.length} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 border-t border-gray-100 pt-3 md:grid-cols-4 dark:border-purple">
        <Field
          label={t('preview.numeroPrevisto')}
          value={
            preview.numeroPrevisto
              ? `${preview.numeroPrevisto}${sezionale?.sigla ? `/${sezionale.sigla}` : ''}`
              : undefined
          }
        />
      </div>
      <div className="mt-3">
        <span className="text-xs uppercase text-gray-400">
          {t('preview.sezionale')}
        </span>
        {sezionali.length > 0 ? (
          <select
            className="mt-1 block w-full max-w-md rounded border border-gray-200 bg-white px-2 py-1 text-sm dark:border-purple dark:bg-grey dark:text-white"
            value={sezionale?.id ?? ''}
            onChange={(e) => onSezionaleChange(Number(e.target.value))}
          >
            {sezionali.map((s) => (
              <option key={s.id} value={s.id}>
                {sezionaleLabel(s)}
              </option>
            ))}
          </select>
        ) : (
          <div className="text-sm">
            {sezionale ? sezionaleLabel(sezionale) : '—'}
          </div>
        )}
        {sezionale?.incerto && (
          <p className="mt-1 text-xs text-amber-700">
            {t('preview.sezionaleIncerto')}
          </p>
        )}
        {preview.anagrafica?.ambigua && (
          <p className="mt-1 text-xs text-amber-700">
            {t('preview.anagraficaAmbigua')}
          </p>
        )}
        {preview.anagrafica?.senzaNazione && (
          <p className="mt-1 text-xs text-amber-700">
            {t('preview.anagraficaSenzaNazione')}
          </p>
        )}
      </div>
    </div>
  );
};

/** Colore della card di esito: creata / saltata / in errore. */
const resultTone = (result: AutofatturaCreateResult): string => {
  if (result.success) return 'border-green-300 bg-green-50';
  if (result.skipped) return 'border-amber-300 bg-amber-50';
  return 'border-red-300 bg-red-50';
};

const ResultMessage: React.FC<
  { result: AutofatturaCreateResult } & CardTProps
> = ({ result, t }) => {
  if (result.success) {
    return (
      <div className="text-sm text-green-700">
        {t('result.success', { docNumber: result.docNumber })}
        {result.anagraficaCreatedId
          ? ` · ${t('result.anagraficaCreated', { id: result.anagraficaCreatedId })}`
          : ''}
      </div>
    );
  }
  if (result.skipped) {
    return (
      <div className="text-sm text-amber-800">
        {t('result.skipped', { docNumber: result.docNumber })}
      </div>
    );
  }
  return (
    <div className="text-sm text-red-600">
      {t('result.error')}: {result.error}
    </div>
  );
};

const ResultCard: React.FC<
  { result: AutofatturaCreateResult } & CardTProps
> = ({ result, t }) => (
  <div className={`rounded-lg border p-4 dark:bg-grey ${resultTone(result)}`}>
    <div className="font-medium">{result.fileName}</div>
    <ResultMessage result={result} t={t} />
  </div>
);

export default AutofatturePage;
